import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { RoleBadge } from './ui/RoleBadge';
import { roleColors, type UserRole } from '../utils/roleColors';
import { NotificationBell, NotificationDropdown, ToastContainer } from './notifications';
import { AgentStatusBar } from './ai';
import { DemoFlowNavigator, PoweredByAzure, PersonaIndicator } from './demo';
import {
  Home,
  Folder,
  FileBox,
  Upload,
  History,
  Shield,
  LogOut,
  Menu,
  X,
  ClipboardList,
  ClipboardCheck,
  ScanEye,
  Scale,
  Share2,
  Archive,
  Gavel,
  Presentation,
  Layers,
  Brain,
} from 'lucide-react';
import { useState } from 'react';

const navigation = [
  { name: 'Demo Hub', href: '/demo-hub', icon: Presentation },
  { name: 'Architecture', href: '/diagrams', icon: Layers },
  { name: 'AI Agents', href: '/ai-agents', icon: Brain, roles: ['SystemAdmin', 'Judge'] },
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Cases', href: '/cases', icon: Folder, roles: ['LawEnforcement', 'Attorney', 'CaseManager', 'SystemAdmin', 'Judge'] },
  { name: 'Evidence', href: '/evidence', icon: FileBox, roles: ['LawEnforcement', 'Attorney', 'CaseManager', 'SystemAdmin'] },
  { name: 'Upload Evidence', href: '/evidence/upload', icon: Upload, roles: ['LawEnforcement', 'Attorney', 'CaseManager', 'SystemAdmin'] },
  { name: 'Evidence Intake', href: '/evidence/intake', icon: ClipboardList, roles: ['CaseManager', 'SystemAdmin'] },
  { name: 'My Cases', href: '/my-cases', icon: Folder, roles: ['PublicSubmitter'] },
  { name: 'Court Filing', href: '/filing', icon: Scale, roles: ['Attorney', 'CaseManager', 'Judge'] },
  { name: 'Pending Filings', href: '/pending-filings', icon: ClipboardCheck, roles: ['CaseManager', 'SystemAdmin'] },
  { name: 'Discovery', href: '/discovery', icon: Share2, roles: ['Attorney', 'CaseManager', 'SystemAdmin'] },
  { name: 'Courtroom', href: '/courtroom', icon: Gavel, roles: ['Attorney', 'CaseManager', 'SystemAdmin', 'Judge'] },
  { name: 'Disposition', href: '/disposition', icon: Archive, roles: ['CaseManager', 'Judge', 'SystemAdmin'] },
  { name: 'Compliance', href: '/compliance', icon: Shield, roles: ['CaseManager', 'SystemAdmin'] },
  { name: 'Pending Submissions', href: '/submissions', icon: ClipboardList, roles: ['CaseManager', 'SystemAdmin'] },
  { name: 'PII Scanner', href: '/pii-demo', icon: ScanEye, roles: ['CaseManager', 'SystemAdmin', 'Judge'] },
  { name: 'Audit Log', href: '/audit', icon: History, roles: ['CaseManager', 'SystemAdmin', 'Judge'] },
];

export default function Layout() {
  const { user, logout, hasRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const filteredNav = navigation.filter((item) => {
    if (!item.roles) return true;
    return hasRole(item.roles);
  });

  // Get role-specific colors for accent
  const userRoleColors = user ? roleColors[user.role as UserRole] : null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-900/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-justice-navy rounded-lg">
                <Shield className="h-6 w-6 text-justice-gold" />
              </div>
              <div>
                <span className="text-justice-navy font-bold text-lg">DEMS</span>
                <p className="text-xs text-gray-500 -mt-0.5">Evidence System</p>
              </div>
            </div>
            <button
              className="lg:hidden text-gray-500"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {filteredNav.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-justice-navy text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-justice-navy'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className={`h-5 w-5 ${isActive ? 'text-justice-gold' : ''}`} />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User info */}
          {user && userRoleColors && (
            <div className={`p-4 border-t ${userRoleColors.border} ${userRoleColors.bg}`}>
              <div className="flex items-center gap-3">
                <div className={`flex-shrink-0 h-10 w-10 rounded-full ${userRoleColors.solid} flex items-center justify-center`}>
                  <span className="text-white font-bold text-sm">
                    {user.name.split(' ').map((n) => n[0]).join('')}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                  <RoleBadge
                    role={user.role as UserRole}
                    department={user.department}
                    size="sm"
                  />
                </div>
              </div>
              <button
                onClick={logout}
                className="mt-3 flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-gray-50/80 backdrop-blur-sm border-b border-gray-200">
          <div className="flex items-center justify-between h-14 px-6">
            <button
              className="lg:hidden text-gray-600"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="hidden lg:block" />
            <div className="flex items-center gap-4">
              {/* Notifications */}
              <div className="relative">
                <NotificationBell
                  onClick={() => setNotificationsOpen(!notificationsOpen)}
                  isOpen={notificationsOpen}
                />
                <NotificationDropdown
                  isOpen={notificationsOpen}
                  onClose={() => setNotificationsOpen(false)}
                  onViewAll={() => {
                    setNotificationsOpen(false);
                    navigate('/notifications');
                  }}
                />
              </div>
              
              {/* Enhanced Persona Indicator for Demo */}
              {user && <PersonaIndicator />}
              
              <div className="flex items-center gap-2 text-xs text-gray-400 border-l pl-4 border-gray-200">
                <span className="h-2 w-2 bg-green-500 rounded-full animate-pulse" />
                Connected
              </div>
            </div>
          </div>
        </header>

        {/* Page content - extra bottom padding for footer */}
        <main className="p-6 lg:p-10 pb-20">
          <Outlet />
        </main>
      </div>
      
      {/* Toast notifications for real-time alerts */}
      <ToastContainer />
      
      {/* Demo Flow Navigator - floating widget */}
      <DemoFlowNavigator />
      
      {/* Powered by Azure Footer */}
      <PoweredByAzure />
      
      {/* AI Agent Status Bar - Only visible to SystemAdmin (Priya) */}
      {user?.role === 'SystemAdmin' && <AgentStatusBar />}
    </div>
  );
}
